import {
  Blocks,
  ChevronsLeft,
  FileText,
  Focus,
  Frame,
  Gauge,
  Layers,
  MessageSquare,
  MessagesSquare,
  PanelLeftOpen,
  Plus,
  ScrollText,
  Search,
  Shapes,
  Star,
  UserRound,
  Zap,
} from "lucide-react";
import type { Card, Moment } from "./types";

/**
 * THE REGISTRY. One entry per first-use moment: when it fires (a presence
 * edge over a `data-onboarding-target` name), an optional `requires` (the
 * edge counts only once those moments are seen — the pane walk waits for
 * Home), an optional `after` (it also fires right after that moment is
 * done, if its first card's target is on screen; no round-1 moment chains,
 * each fires at its own time), and the cards. Copy, glyphs, sides and keys
 * are the design system's (experiment 13, moments.jsx), verbatim.
 *
 * A card: the glyph of what it points at, the target, where the card sits,
 * an optional `before` beat (ring a way in, hold, click it, settle — only
 * while the card's own target is missing), the title, the body, optional
 * keys, and the docs page the words come from.
 *
 * Round 1 is Home and the left rail. The canvas moment (04) is here for the
 * first run's landing, which fires it by name once the canvas holds
 * (`manual`); its keycaps are the ones web-ui's canvas really answers. Later
 * rounds add 05 files (add-mode-verbs, card-placed, FigureReference), 06
 * agent-file (card-agent-doc, FigureAddToWorkspace), 07 share
 * (share-link-access, FigureSharePublish), 08 publish
 * (artifact-publish-button).
 */

/** The docs site's product pages. Null until the deploy's domain is confirmed; no links render before then. */
export const DOCS_BASE: string | null = null;

const doc = (path: string, label: string): Card["docs"] => ({ path, label });

/** What the agent lands on the canvas: a card slides clear of it. */
const AGENT_NODES = ".react-flow__node";

const lens = (
  key: string,
  title: string,
  tint: Moment["tint"],
  card: Omit<Card, "target" | "side" | "align">,
): Moment => ({
  key,
  title,
  fires: `The first time the ${title} view opens`,
  when: { kind: "appears", target: `rail-view-${key}` },
  tint,
  cards: [
    { ...card, target: `rail-view-${key}`, side: "right", align: "start" },
  ],
});

export const MOMENTS: readonly Moment[] = [
  {
    key: "home",
    title: "Home",
    fires: "The first time Home opens after onboarding, or after Maybe later",
    when: { kind: "appears", target: "home-composer", scope: "route" },
    cards: [
      {
        key: "composer",
        icon: Frame,
        target: "home-composer",
        side: "bottom",
        align: "center",
        title: "Start with the work",
        body: "Describe what you're working on. The agent starts a canvas around it, and it opens as a tab up top.",
        docs: doc("workspace/home", "Home, in the docs"),
      },
      {
        key: "views",
        icon: PanelLeftOpen,
        target: "rail-nav",
        side: "bottom",
        align: "start",
        // On a closed pane: ring the mark, hold, open it, and let the ring
        // glide out to the five glyphs; skipped when the pane is open.
        before: {
          target: "rail-expand",
          when: "card-target-missing",
          hold: 800,
          then: "click",
          settle: 500,
        },
        title: "Five views of your work",
        body: "Workspaces holds everything. Chats, Skills, Connectors and Automations are lenses on the same work; the pane below shows whichever you pick.",
        docs: doc("workspace/left-rail", "The left rail, in the docs"),
      },
      {
        key: "collapse",
        icon: ChevronsLeft,
        target: "rail-collapse",
        side: "bottom",
        align: "start",
        // Collapsing the pane is what the card asks for: done, not paused.
        advance: "target-gone",
        title: "Give the canvas the room",
        body: "Collapse the pane when you want the whole window. Your avatar stays put, and search is one shortcut away.",
        keys: [{ label: "Search", keys: ["⌘", "K"] }],
      },
    ],
  },
  {
    key: "workspaces",
    title: "Workspaces",
    fires: "After Home, the next time the pane opens on Workspaces",
    requires: "home",
    when: { kind: "appears", target: "rail-view-workspaces" },
    cards: [
      {
        key: "tree",
        icon: Layers,
        target: "rail-my-workspace",
        side: "right",
        align: "start",
        title: "One tree",
        body: "Folders, canvases and files sit side by side. My Workspace is yours alone; the team workspaces follow below it.",
        docs: doc("workspace/left-rail", "The left rail, in the docs"),
      },
      {
        key: "files",
        icon: FileText,
        target: "rail-my-workspace-items",
        side: "right",
        align: "start",
        // My Workspace may rest closed: ring its toggle, hold, expand it,
        // and let the ring settle on what it holds. Only when the toggle
        // says it is shut: an open row whose tree is still loading reads
        // as missing contents too, and the same toggle would close it.
        before: {
          target: "rail-my-workspace-toggle",
          when: "target-collapsed",
          hold: 700,
          then: "click",
          settle: 450,
        },
        // A first-use tree load is slower than the default wait.
        wait: 5000,
        title: "Files that travel",
        body: "Keep a file here and every canvas and chat can use it. A file made inside a canvas stays with that canvas until you add it to the workspace.",
        docs: doc("files/overview", "Files, in the docs"),
      },
      {
        key: "favorites",
        icon: Star,
        target: "rail-favorites",
        side: "right",
        align: "start",
        title: "Keep the busy ones close",
        body: "Favorites holds the canvases and files you return to. Shared with me collects what others shared with you directly.",
        docs: doc("workspace/folders", "Folders and favorites, in the docs"),
      },
      {
        key: "search",
        icon: Search,
        target: "rail-search",
        // Below the field, not beside it: a card to its right lands on the
        // home composer, which starts 150px past the pane.
        side: "bottom",
        align: "start",
        title: "Search everything",
        body: "Workspaces, canvases, chats and files, from one field. The shortcut opens it from anywhere, pane open or closed.",
        keys: ["⌘", "K"],
        docs: doc("workspace/search", "Search, in the docs"),
      },
    ],
  },
  {
    key: "account",
    title: "Your account",
    fires: "After your first visit to Settings",
    when: { kind: "disappears", target: "settings-dialog" },
    cards: [
      {
        key: "identity",
        icon: UserRound,
        target: "footer-account",
        side: "right",
        align: "end",
        title: "You, and your organization",
        body: "Appearance, the canvas grid and switching organizations live here. Archive holds what you removed until you say otherwise.",
        docs: doc("account/your-account", "Your account, in the docs"),
      },
      {
        key: "usage",
        icon: Gauge,
        target: "footer-usage",
        clear: "rail-pane",
        side: "right",
        align: "end",
        // No subscription or usage, no meter: no beat opens the pane for it.
        applies: (f) => f.usageMeter,
        // The meter lives in the pane: on a closed pane, ring the mark, open
        // it, and land on the meter; with the pane open and no meter (no
        // subscription) there is no beat and the card is skipped.
        before: {
          target: "rail-expand",
          when: "card-target-missing",
          hold: 800,
          then: "click",
          settle: 500,
        },
        title: "The ring is your usage",
        body: "It fills as the agent works and warns at three quarters. Settings sits behind the gear.",
        docs: doc("account/usage", "Usage, in the docs"),
      },
    ],
  },
  {
    key: "canvas",
    title: "Your first canvas",
    fires: "The first canvas, after the first run's primer has opened it",
    manual: true,
    cards: [
      {
        key: "tools",
        icon: Shapes,
        target: "canvas-object-tools",
        side: "top",
        align: "center",
        clearSelector: AGENT_NODES,
        title: "The tools",
        body: "Notes, Docs, Diagrams, and Data with your connected apps. What's on the canvas becomes context you and the agent can work with.",
        keys: [
          { label: "Note", keys: ["N"] },
          { label: "Doc", keys: ["D"] },
          { label: "Section", keys: ["S"] },
          { label: "Text", keys: ["T"] },
        ],
        docs: doc("canvas/overview", "The canvas, in the docs"),
      },
      {
        key: "controls",
        icon: Focus,
        target: "canvas-controls",
        side: "bottom",
        align: "end",
        clearSelector: AGENT_NODES,
        title: "Cursor, grid, zoom",
        body: "Pointer or hand, the grid style, alignment guides and zoom. Recenter brings everything back into view.",
        // The design system's card says `1`; web-ui fits everything on ⌘1.
        keys: [{ label: "Fit everything", keys: ["⌘", "1"] }],
      },
      {
        key: "chat",
        icon: MessageSquare,
        target: "agent-chat-toggle",
        side: "top",
        align: "center",
        clearSelector: AGENT_NODES,
        title: "Quick Chat",
        body: "Press C anywhere on the canvas to ask the agent about what's selected. The chat is yours; what it makes lands on the canvas for everyone.",
        keys: [{ label: "Quick Chat", keys: ["C"] }],
        docs: doc("agent/quick-chat", "Quick Chat, in the docs"),
      },
      // A canned run's fourth card, over the tab strip's + (the first run
      // asks for it by name; a custom run walks three). Provisional copy,
      // Matt's to replace; no shortcut opens a new canvas in web-ui, so no
      // keys; docs off until DOCS_BASE.
      {
        key: "new",
        icon: Plus,
        target: "new-canvas",
        side: "bottom",
        align: "start",
        title: "Start your own canvas",
        body: "This one was a demonstration. Open a new canvas, drop in your own files or connect a source, and ask the agent what you need.",
      },
    ],
  },
  lens("chats", "Chats", "oat", {
    key: "chats",
    icon: MessagesSquare,
    title: "Chats are yours",
    body: "Every chat is private to the person who started it. Pin the ones you return to; each opens as a tab.",
    docs: doc("agent/sessions", "Chats and sessions, in the docs"),
  }),
  lens("skills", "Skills", "meadow", {
    key: "skills",
    icon: ScrollText,
    title: "Teach it once",
    body: "A Skill is a set of instructions the agent applies on its own. Type / in any chat to call one; Auto skills fire whenever the ask matches.",
    keys: [{ label: "In a chat", keys: ["/"] }],
    docs: doc("skills/overview", "Skills, in the docs"),
  }),
  lens("connectors", "Connectors", "apricot", {
    key: "connectors",
    icon: Blocks,
    title: "Bring your tools in",
    body: "Connect Slack, Drive or HubSpot, then expand one here and drag records onto a canvas. The agent reads them live, under your permissions.",
    docs: doc("connectors/overview", "Connectors, in the docs"),
  }),
  lens("automations", "Automations", "sea", {
    key: "automations",
    icon: Zap,
    title: "Work that runs without you",
    body: "An automation is a place, instructions and a schedule. Active runs next at the time shown; Paused keeps its schedule until you resume it.",
    docs: doc("automations/overview", "Automations, in the docs"),
  }),
];
